import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IStudent } from '../../../models/students.model';
import StudentName from './Name';
import moment from 'moment';
import _ from 'lodash';
import { Table, TableColumnsType } from 'antd';

import Button from '../../Button';
import Pagination from '../../Pagination';
import config from '../../../lib/config';
import { IGetStudentMemos, IStudentMemo } from '../../../models/studentsMemos.model';
import useAxios from '../../../hooks/useAxios';
import { deleteStudentMemo, getStudentMemos } from '../../../api/studentsMemos.api';
import Loader from '../../Loader';
import ErrorResponse from '../../ErrorResponse';
import EditMemoModal from './modals/EditMemoModal';
import ConfirmModal from '../../modals/ConfirmModal';
import useNotification from '../../../hooks/useNotification';
import useAuth from '../../../hooks/useAuth';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/rootReducer';

interface IProps {
  student: IStudent|undefined;
}

const StudentMemoTab: FC<IProps> = ({
  student,
}) => {
  const { t } = useTranslation();
  const { writeAuthYn } = useAuth();
  const { auth } = useSelector((state: RootState) => state.auth);
  const { successNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetStudentMemos>();
  const { data: deleteRes, isLoading: deleteIsLoading, error: deleteError, execute: deleteExecute } = useAxios();

  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState('');
  const [data, setData] = useState<IStudentMemo[]>([]);
  const [editMemoVisible, setEditMemoVisible] = useState(false);
  const [record, setRecord] = useState<IStudentMemo>();
  const [confirmDeleteVisible, setConfirmDeleteVisible] = useState(false);

  useEffect(() => {
    if (student) {
      doGetStudentMemos();
    }
  }, [student]);

  useEffect(() => {
    if (res && !isLoading) {
      setData(res.data);
    }
  }, [res, isLoading]);

  useEffect(() => {
    if (deleteRes && !deleteIsLoading) {
      setConfirmDeleteVisible(false);
      doGetStudentMemos();
      successNotification(t('common.message.delete'));
    }
  }, [deleteRes, deleteIsLoading]);

  const doGetStudentMemos = () => {
    if (!student) return;
    execute(getStudentMemos(student.campusCode, student.studentCode));
  }

  const onSearch = () => {
    let _data: any = _.cloneDeep(res?.data);
    _data = _data.filter((item: any) => item.memo.indexOf(keyword) > -1);
    setData(_data);
  }

  const onDelete = () => {
    if (!record) return;
    deleteExecute(deleteStudentMemo(record.memoCode));
  }

  const columns: TableColumnsType<IStudentMemo> = [
    {
      title: t('common.column.date'),
      dataIndex: 'regDate',
      key: 'regDate',
      width: '150px',
      sorter: (a, b) => a.regDate?.localeCompare(b.regDate),
      render: (value: string) => moment(value).format('YYYY.MM.DD HH:mm:ss')
    },
    {
      title: t('common.column.writer'),
      dataIndex: 'korName',
      key: 'korName',
      width: '100px',
      sorter: (a, b) => a.korName?.localeCompare(b.korName)
    },
    {
      title: t('common.column.contents'),
      dataIndex: 'memo',
      key: 'memo',
      align: 'center',
      sorter: (a, b) => a.memo?.localeCompare(b.memo),
      render: value => <div className="text-left">{value}</div>
    },
    {
      title: '',
      dataIndex: 'memoCode',
      key: 'memoCode',
      align: 'center',
      width: '150px',
      render: (value, record) => (
        <div className="comm-btn__area justify-content-center">
          <Button
            className="btn-basic small pink-line"
            onClick={() => {
              setRecord(record);
              setEditMemoVisible(true);
            }}
            text={t('common.button.edit')}
            writeAuthYn={writeAuthYn}
            disabled={record.regMemberCode !== auth.memberCode}
          />
          <Button
            className="btn-basic small gray"
            onClick={() => {
              setRecord(record);
              setConfirmDeleteVisible(true);
            }}
            text={t('common.button.delete')}
            writeAuthYn={writeAuthYn}
            disabled={record.regMemberCode !== auth.memberCode}
          />
        </div>
      ),
    },
  ];

  return (
    <>
      {isLoading || deleteIsLoading ? <Loader /> : null}
      <ErrorResponse error={error} />
      <ErrorResponse error={deleteError} />

      <StudentName student={student} />
      <div className="comm-bx__space">
        <div className='comm-btn__area right'>
          <div className="w-240p">
            <input
              type="text"
              className="form-control input-text"
              placeholder={t('student.student.placeholder4')}
              value={keyword}
              onChange={e => setKeyword(e.target.value)}
              onKeyPress={e => { if (e.key === 'Enter') onSearch() }}
            />
          </div>
          <div className="comm-btn__area">
            <Button
              className='btn-basic small black'
              onClick={onSearch}
              text={t('common.button.search')}
            />
          </div>
        </div>
      </div>
      <Table
        className='comm-table__area'
        columns={columns}
        dataSource={data}
        pagination={{
          current: page,
          pageSize: config.pageSize
        }}
      />
      <div className="comm-bx__between">
        <div className="common-paging center">
          <Pagination
            count={data.length}
            page={page}
            setPage={setPage}
          />
        </div>
      </div>

      {editMemoVisible && (
        <EditMemoModal
          visible={editMemoVisible}
          setVisible={setEditMemoVisible}
          record={record}
          doGetStudentMemos={doGetStudentMemos}
        />
      )}
      {confirmDeleteVisible && (
        <ConfirmModal
          visible={confirmDeleteVisible}
          setVisible={setConfirmDeleteVisible}
          onConfirm={onDelete}
          titleText={t('system.menu.title6')}
          messageText={t('system.menu.text3')}
          confirmText={t('common.button.delete')}
          cancelText={t('common.button.cancel')}
        />
      )}
    </>
  );
}

export default StudentMemoTab;
